import * as React from 'react';
import { useIntl } from 'react-intl';
import {
  CmdModal,
  CmdModalFooterButton,
  CmdSimpleSelect,
} from '@commander-services/gui-components';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import localforage from 'localforage';
import { Controller, useForm } from 'react-hook-form';
import FormikValidatorService from '../../../Services/FormikValidatorService';
import PermissionsService, {
  PERMISSION_GDPR_PROFILE_LIST,
} from '../../../Services/PermissionsService';
import usePageWithForm from '../../../hooks/usePageWithForm';
import { gdprSettingsAtom } from '../../../store/recoil/AppState';
import { selectedCustomersAtom } from '../../../store/recoil/customers';
import { transformErrors } from '../../../Services/HookFormService';
import { getRequest, putRequest } from '../../../Services/HttpService';
import { AxiosResponse } from 'axios';

interface IFormProps {
  closeCallback: () => void;
  userIds: number[];
  updateTable?: (() => void) | undefined;
  customerId?: number | null;
  options: any;
  customerLabel?: string;
  fullname?: string;
}

export default function Form(props: IFormProps): JSX.Element {
  usePageWithForm();
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
  const { formatMessage: f } = useIntl();
  const [loader, setLoader] = React.useState<boolean>(false);
  const setGdprSettings = useSetRecoilState<Record<string, any>>(gdprSettingsAtom);

  const validateFields = (values) => {
    let formErrors = {};
    formErrors = FormikValidatorService.validateByValidationApiResponse(values, {
      profileId: [
        {
          param: 'required',
          value: true,
          message: f({ id: 'form.required' }),
        },
      ],
    });
    return transformErrors(formErrors);
  };

  const {
    handleSubmit,
    control,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<{ profileId: string }>({
    mode: 'onChange',
    defaultValues: {
      profileId: '',
    },
    resolver: async (data) => {
      const formErrors = validateFields(data);
      return {
        values: formErrors && Object.keys(formErrors).length ? {} : data, // if there's an error, return empty values
        errors: formErrors,
      };
    },
  });

  const onSubmit = async (values) => {
    setLoader(true);
    values.userId = props.userIds;
    values.customerId = props.customerId;
    const response: AxiosResponse | false = await putRequest(`v1/gdpr/user/assign-profile`, values, { returnAxiosResponse: true });
    setLoader(false);

    if (response && response.data && response.status === 200 && props.updateTable) {
      props.closeCallback();
      props.updateTable();
      if (
        PermissionsService.hasAccess(
          PERMISSION_GDPR_PROFILE_LIST,
          ['create', 'update'],
          selectedCustomers
        )
      ) {
        const res: AxiosResponse | false = await getRequest(`v1/gdpr/profile/${values.profileId}`, { returnAxiosResponse: true });
        if (res && res.data.profile) {
          const { profile } = res.data;
          const thisUserId = localStorage.getItem('userId');
          const profiles = await localforage.getItem<Record<string, any>[]>('gdprSettings');
          if (Array.isArray(profiles) && profiles.length > 0) {
            const profilesArr = profiles.map((i: any) => {
              if (
                String(i.customerId) === String(props.customerId) &&
                values.userId.includes(String(thisUserId))
              ) {
                return { ...profile, customerId: props.customerId };
              }
              return i;
            });
            setGdprSettings(profilesArr);
          }
        }
      }
    }
  };

  const buttons: CmdModalFooterButton[] = [
    {
      id: 'close-modal',
      type: 'button',
      className: 'e-button',
      title: f({ id: 'alarms.modal.rule.button.close' }),
      tooltip: f({ id: 'alarms.modal.recipient.close.tooltip' }),
      closeCallback: props.closeCallback,
    },
    {
      id: 'gdprlist-form-submit',
      type: 'submit',
      className: 'e-button e-button--gray',
      title: f({ id: 'alarms.modal.recipient.save' }),
      tooltip: f({ id: 'gdprProfile.modal.save.tooltip' }),
      submitCallback: handleSubmit(onSubmit),
    },
  ];

  return (
    <div className="b-modal-overflow">
      <CmdModal
        id="profile-form-modal"
        // title={`${f({ id: 'gdprProfile.modal.profileChanging.title' })} (+${props.userIds.length} ${getSuffix(props.userIds.length)})`}
        title={
          !props.fullname
            ? `${f({ id: 'gdprProfile.modal.profileChanging.title' })} (${f({
                id: 'gdprProfile.chosenUsers',
              })}: ${props.userIds.length}${
                props.customerLabel ? ` (${props.customerLabel})` : ''
              })`
            : `${f({ id: 'gdprProfile.modal.profileChanging.title' })} (${props.fullname}${
                props.customerLabel ? ` (${props.customerLabel})` : ''
              })`
        }
        footerButtons={buttons}
        loader={loader}
      >
        <Controller
          name="profileId"
          control={control}
          rules={{ required: errors.profileId ? errors.profileId.message : undefined }}
          render={({ field }) => (
            <CmdSimpleSelect
              {...field}
              name="profileId"
              id="profileId"
              label={f({ id: 'gdprProfile.modal.profileChanging.label' })}
              introOption={f({ id: 'form.choose' })}
              options={props.options}
              required={true}
              onChangeCallback={(value: string) => {
                setValue('profileId', value);
                trigger('profileId');
              }}
              error={errors.profileId?.message}
              touched={true}
            />
          )}
        />
      </CmdModal>
    </div>
  );
}
